// src/components/shell/Sidebar/SidebarLocationLinks.tsx
// Sidebar group surfacing per-location tool links (GHL, calendar, etc.).
//
// Slice A of docs/planning/activity_dock_workflow_facilitation.md §6.
// Previously these lived as a collapsible card inside `LeadsModeBanners`,
// which stretched the Leads-mode header and felt disconnected from the
// other navigation surfaces. Lifting them into the sidebar puts them
// alongside the help / profile / settings popovers — same global-nav
// cluster, same popover pattern, same rail/desktop/mobile layout
// affordances.
//
// Behavior:
//   • Returns `null` when `salesperson === null` (no salesperson = no
//     personalized link substitution to surface; matches the original
//     collapsible card's `{salesperson ? … : null}` gate).
//   • Per-location URL substitution: `link.perLocation[locationId] ??
//     link.baseUrl`, driven by `useScopedOrganizationId` →
//     `useMergedOrgToolLinks` (same data path the old card used).
//   • Popover uses `useRailPopover` so it picks up the standard
//     right-of-rail / below-on-mobile placement + outside-click + escape
//     dismissal already used by HelpPopover / ProfilePopover / etc.

import { createPortal } from "react-dom";
import { SidebarRailButton } from "./SidebarRailButton";
import { railPopoverPanelStyle, useRailPopover } from "@/components/shell/popovers/useRailPopover";
import { useMergedOrgToolLinks } from "@/hooks/useMergedOrgToolLinks";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";

export type SidebarLocationLinksProps = {
  expanded: boolean;
  salesperson: { name: string } | null;
  /**
   * Selected workspace location. `id` is the canonical location key
   * used for per-location URL substitution (e.g. `lot_ravenel`); `name`
   * is the user-facing display string shown as the popover subtitle.
   */
  location: { id?: string | null; name: string } | null;
  /** Closes the mobile drawer (and similar parent surfaces) after the user clicks through to an external link. */
  onAfterAction?: () => void;
};

export function SidebarLocationLinks({
  expanded,
  salesperson,
  location,
  onAfterAction,
}: SidebarLocationLinksProps) {
  const scopedOrgId = useScopedOrganizationId();
  const { toolLinks } = useMergedOrgToolLinks(scopedOrgId);
  const { brandName } = useOrgSettings();
  const { open, setOpen, toggle, triggerRef, panelRef, position } = useRailPopover();

  // No-op gate: same condition as the previous LeadsModeBanners card.
  // A user with no associated salesperson has no per-rep link
  // substitution worth surfacing; better to hide the row entirely than
  // render a half-empty popover trigger.
  if (!salesperson) return null;

  const locationId = location?.id ?? "";

  return (
    <div className="border-t border-gray-200 dark:border-gray-800 px-2 py-2">
      <SidebarRailButton
        ref={triggerRef}
        expanded={expanded}
        glyph="🔗"
        label="Location Links"
        active={open}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
      />
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Location Links"
              className="rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-zinc-950 dark:text-gray-100"
              style={railPopoverPanelStyle(position)}
            >
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Location Links
                  </div>
                  <div className="mt-0.5 truncate text-sm font-semibold">
                    {location?.name ?? "No location selected"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close location links"
                  className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  ✕
                </button>
              </div>
              <div className="px-4 py-3 text-sm text-gray-800 dark:text-gray-100">
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                  Quick access to your core {brandName || "company"} systems. Opens in a new tab.
                </p>
                {toolLinks.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    No links configured yet. An admin can add them in org settings.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {toolLinks.map((link) => {
                      const href =
                        (locationId && link.perLocation && link.perLocation[locationId]) ||
                        link.baseUrl;
                      return (
                        <a
                          key={link.id}
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => {
                            setOpen(false);
                            onAfterAction?.();
                          }}
                          className="group flex flex-col justify-between rounded-xl border border-gray-200 px-3 py-2 transition-colors hover:border-blue-400 hover:bg-blue-50 dark:border-gray-700 dark:hover:border-blue-500 dark:hover:bg-blue-950/40"
                        >
                          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                            {link.label}
                          </span>
                          <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            {link.description}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
